import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup } from "@mui/material";

import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useApi } from "../hooks/useApi";
import LazyLoader from "./LazyLoader";
import SearchField from "./SearchField";

/**
 * Implements a dialog that displays options to choose from specified through optionsURL as prop.
 * Lazy-loads the options.
 * @param {*} param0 
 * @returns 
 */
export default function MergeList({ handleClose, handleAccept, title, isOpen, initialID, optionsURL, fieldName }) {
  const [value, setValue] = useState(null);
  const radioGroupRef = useRef(null);

  const [data, setData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [endReached, setEndReached] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [query, setQuery] = useState("");

  const api = useApi();

  /**
   * Whenever the page is changed, fetch new data (except for first page).
   */
  useEffect(() => {
    // do not fetch first page here
    if (page === 1) return;
    fetchData(page, query);
  }, [page]);

  /**
   * Clear the data and start new fetch.
   */
  useEffect(() => {
    // clear the selected radio-button
    setValue(null);
    setPage(1);
    setEndReached(false);
    fetchData(1, query);
  }, [query]);

  /**
   * Fetches the specified page from the server with query.
   * @param {*} page 
   * @param {*} query 
   */
  const fetchData = (page, query) => {
    setIsLoading(true);
    api
      .get(`${optionsURL}&search=${query}&page=${page - 1}&size=${pageSize}`)
      .then(response => {
        // filter data such that it does not contain the options with the specified initialID
        const content = response.data.content.filter(d => d.id !== initialID);
        if (page === 1) {
          setData(content);
        } else {
          setData(old => [...old, ...content]);
        }
        setEndReached(response.data.last);
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  /**
   * Merge is cancelled.
   */
  const handleCancel = () => {
    handleClose();
  };

  /**
   * Merge is accepted.
   * If no value was selected, do the same thing as if it was cancelled.
   */
  const handleOk = () => {
    if (value) {
      handleAccept(value);
    } else {
      handleClose();
    }
  };

  /**
   * Update the value.
   * @param {*} event 
   */
  const handleChange = (event) => {
    setValue(event.target.value);
  };

  /**
   * The options to select from.
   */
  const options = useMemo(() => {
    return data.map((option) => (
      <FormControlLabel
        value={option.id}
        key={option.id}
        control={<Radio />}
        label={option[fieldName]}
      />
    ))
  }, [data]);

  return (
    <Dialog
      maxWidth="md"
      TransitionProps={{ onEntering: handleEntering }}
      open={isOpen}
      onClose={handleClose}
      fullWidth={true}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <SearchField onChange={(value) => setQuery(value)} />
        <Box sx={{ width: "100%", height: "800px", margin: 0, padding: 0 }}>
          <LazyLoader
            onLoad={() => setPage(prev => prev + 1)}
            loading={isLoading}
            endReached={endReached}
          >
            <RadioGroup
              ref={radioGroupRef}
              aria-label="options"
              name="options"
              value={value}
              onChange={handleChange}
            >
              {options}
            </RadioGroup>
          </LazyLoader>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={handleCancel} >
          <FormattedMessage id="ConfirmationDialog.no" />
        </Button>
        <Button variant="contained" color="success" onClick={handleOk} autoFocus>
          <FormattedMessage id="ConfirmationDialog.yes" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};